import {
  ALL_MODULES,
  AccessControl,
  ApiProgressLoader,
  COMMON_CONSTANTS,
  EventEmitter,
  Loader,
  getCurrentUserHook,
  getDownloadURL,
  getTranslatedText,
  useIntl,
  useIsAuthorisedHook,
  useProduct,
  useSiteListForDropdown,
  useUserMSPCheckHook,
  withErrorBoundary
} from '@nile/components';
import { Tooltip } from 'react-clone-tippy';
import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  Avatar,
  Link,
  TextStyle,
  Popover,
  Card,
  Button,
  Dropdown
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import Tippy from '@tippyjs/react';
import { FormattedMessage } from 'react-intl';
import { isEmpty, isEqual } from 'lodash';
import { useCopyToClipboard } from '@nile/mangifera/src/Pages/MigrateDataPage/useCopyToClipboard';
import LoadingSuccess from '@nile/components/src/Svg/AnimatedSvg/LoadingSuccess.svg';
import {
  AccountClient,
  GET_BASIC_CLIENT_DATA,
  GET_USERS_ASSOCIATIONS_LIST,
  TempAccountClient
} from '@nile/graphql/src';
import { GET_BUSINESS_FUNCTION_LIST } from '@nile/itsm/src/graphql';
import { useQuery } from '@apollo/client';
import { GET_CLIENT_USER } from '@nile/graphql/src/Query/client';
import { GET_CLIENT_DATA } from '@nile/graphql/src/Query/ticket';
import { useStateList } from '@nile/components/src/Constants/stateslist/getstates';
import {
  DropdownIndicator,
  IndicatorSeparator
} from '@nile/components/src/Pebbles/select';
import Select from 'react-select';
import { useLazyQuery } from '@apollo/client';
import { UserSelect } from '@nile/mangifera/src/Components/Ticket';
import ChangeConfirmationModal from '@nile/rmm/src/modules/asset-management/components/sub-pages/asset-management-view-list/ChangeConfirmationModal';

const getRequesterName = (requester) => {
  if (requester?.name) {
    return requester?.name;
  } else if (requester?.firstName && requester?.lastName) {
    return `${requester?.firstName} ${requester?.lastName}`;
  } else if (requester?.firstName && !requester?.lastName) {
    return requester?.firstName;
  } else {
    return null;
  }
};

const getAddress = (site) => {
  const countries = useStateList('country');
  const states = useStateList('states');
  const { line1, line2, line3, stateCode, countryCode, postalCode } = site;
  let address;
  if (line1) {
    if (address) {
      address = `${address}, ${line1}`;
    } else {
      address = `${line1}`;
    }
  }
  if (line2) {
    if (address) {
      address = `${address}, ${line2}`;
    } else {
      address = `${line2}`;
    }
  }
  if (line3) {
    if (address) {
      address = `${address}, ${line3}`;
    } else {
      address = `${line3}`;
    }
  }
  if (stateCode) {
    const state =
      states?.find(
        (st) => st.isoCode === stateCode && st.countryCode === countryCode
      )?.name || '';
    if (address) {
      address = `${address}, ${state}`;
    } else {
      address = `${state}`;
    }
  }
  if (countryCode) {
    const country =
      countries?.find((st) => st.isoCode === countryCode)?.name || '';
    if (address) {
      address = `${address}, ${country}`;
    } else {
      address = `${country}`;
    }
  }
  if (postalCode) {
    if (address) {
      address = `${address}, ${postalCode}`;
    } else {
      address = `${postalCode}`;
    }
  }

  return address;
};

const Heading = (props) => {
  return (
    <div className="flex g10 mb5 fsize12 actr">
      <div className="flex">
        <SVGIcon name={props?.icon} size="sm" />
      </div>
      <TextStyle variation="subdued" uppercase>
        <div>
          <FormattedMessage id={props?.heading} />
        </div>
      </TextStyle>
    </div>
  );
};

function Logo(props) {
  const { client } = props;
  const { logoFileName, accountId } = client;
  const module = ALL_MODULES.clientAvatar;
  const details = {
    fileName: logoFileName,
    clientId: accountId
  };
  const downloadUrl = getDownloadURL({ module, details });

  if (downloadUrl) {
    return (
      <div className="flex center">
        <img
          className="ticket-header-client-logo maxW40"
          src={downloadUrl}
          url={downloadUrl}
          alt="loading"
        />
      </div>
    );
  } else {
    return <></>;
  }
}

const TippyContent = (props) => {
  const intl = useIntl();
  const requesterName = getRequesterName(props?.requester);
  const [isCopied, copyToClipboard] = useCopyToClipboard();
  const [requester, setRequester] = useState(props?.requester);
  const [department, setDepartment] = useState(props?.department);
  const [client, setClient] = useState(props?.client);
  const address = props?.site && getAddress(props?.site);
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);

  const isClientUserAuthorised = useIsAuthorisedHook({
    operator: 'OR',
    roles: ['users.view', 'employees.view']
  });
  const isAllClientsAuthorised = useIsAuthorisedHook('allClients.allow');
  const isForgottenRequester = props?.requester?.forgotten;

  const { loading, error, data: userData } = useQuery(GET_CLIENT_USER, {
    variables: { userId: props?.requester?.userId, userCustomFields: false },
    client: TempAccountClient,
    fetchPolicy: 'cache-and-network',
    skip:
      !props?.requester?.userId ||
      !isClientUserAuthorised ||
      isForgottenRequester
  });

  const {
    loading: clientDataLoading,
    error: clientError,
    data: clientData
  } = useQuery(GET_CLIENT_DATA, {
    variables: { clientId: props?.client?.accountId },
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    skip:
      !isMSPProduct && (!props?.client?.accountId || !isAllClientsAuthorised)
  });

  useEffect(() => {
    if (!isClientUserAuthorised) {
      setRequester(props?.requester);
    }

    if (!isAllClientsAuthorised) {
      setClient(props?.client);
    }
  }, []);

  useEffect(() => {
    if (!isMSPProduct) {
      setDepartment(props?.department);
    }
  }, [props.department]);

  useEffect(() => {
    if (userData?.getClientUser) {
      setRequester(userData?.getClientUser);
    }
  }, [userData]);

  useEffect(() => {
    if (clientData?.getClient) {
      setClient(clientData?.getClientta);
    }
  }, [clientData]);

  if (
    (isEmpty(requester) || isEmpty(clientData)) &&
    (loading || clientDataLoading)
  ) {
    return (
      <Card border="secondary" noShadow padding="sizeSmall">
        <Loader />
      </Card>
    );
  }

  return (
    <Card border="secondary" noShadow padding="sizeSmall">
      {!isEmpty(requester) && (
        <div className="mb20">
          <div>
            <div className="flex g10 mb10">
              <div>
                <Avatar
                  initials={requesterName?.[0] || null}
                  size="extraSmall"
                />
              </div>
              <TextStyle weight="SemiBold">
                <div>{requesterName}</div>
              </TextStyle>
              {requester?.contactNumber && (
                <>
                  <div className="flex opacity5">
                    <SVGIcon name="Phone" />
                  </div>
                  <TextStyle>{requester?.contactNumber}</TextStyle>
                </>
              )}
            </div>

            <div className="flex g10 actr">
              <div className="flex opacity5">
                <SVGIcon name="Email" />
              </div>
              <Link removeUnderline>
                <div className="w100 ellipsis">{requester?.email}</div>
              </Link>
              <div
                className="flex pointer"
                onClick={() => copyToClipboard(requester?.email)}
              >
                {isCopied ? (
                  <div className="flex">
                    <SVGIcon name="LoadingSuccessAnimated" size="sm" />
                  </div>
                ) : (
                  <SVGIcon name="CopyClipboard" color="#4526FF" />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!isEmpty(client) && isMSPProduct && (
        <div className="mb20">
          <div>
            <div className="flex g10 actr">
              {client?.logoFileName ? (
                <Logo client={client} />
              ) : (
                <div className="flex opacity5">
                  <SVGIcon name="ClientLogo" />
                </div>
              )}
              <div>
                <TextStyle weight="SemiBold">{client?.name}</TextStyle>
              </div>
              {client?.contactNumber && (
                <div>
                  <div className="flex opacity5">
                    <SVGIcon name="Phone" />
                  </div>
                  <div>
                    <TextStyle>{client?.contactNumber}</TextStyle>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {!isEmpty(department) && !isMSPProduct && (
        <div className="mb20">
          <div>
            <div className="flex g10 actr">
              <div className="flex opacity5">
                <SVGIcon name="Department" />
              </div>
              <div>
                <TextStyle weight="SemiBold">{department?.name}</TextStyle>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isEmpty(props?.site) && (
        <div className="flex g5">
          <div className="flex opacity5">
            <SVGIcon name="Location" />
          </div>
          <div>
            <div className="mb5">
              <TextStyle weight="SemiBold">{props?.site?.name}</TextStyle>
            </div>
            <div>
              {address && (
                <div>
                  <TextStyle weight="SemiBold">{address}</TextStyle>
                </div>
              )}
            </div>
            {props?.site?.contactNumber && (
              <div>
                <TextStyle weight="SemiBold">
                  {props?.site?.contactNumber}
                </TextStyle>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

const ClientDetailsEdit = (props) => {
  const {
    client,
    requester,
    site,
    onSave,
    onCancel,
    department,
    requesterClearable,
    isUpdateDeptPayload,
    assetUiSection,
    hideCreateRequester = false,
    popupFieldsToShow,
    hidePopupIcons = false,
    disableClientEdit = false,
    disableSiteEdit = false,
    showOnlyActiveClients
  } = props;
  const intl = useIntl();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const currentProduct = useProduct();

  const [siteOptions, setSiteOptions] = useState(null);
  const [clientOptions, setClientOptions] = useState(null);
  const [requesterOptions, setRequesterOptions] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState(null);

  const [selectedClient, setSelectedClient] = useState(client);
  const [selectedSite, setSelectedSite] = useState(site);
  const [selectedRequester, setSelectedRequester] = useState(requester);
  const [selectedDepartment, setSelectedDepartment] = useState(department);
  const isRequesterChanged = useRef(false);
  useEffect(() => {
    if (!clientOptions && client?.name) {
      setClientOptions([
        {
          name: client?.name,
          value: client?.name,
          label: client?.name
        }
      ]);
    }
  }, []);

  const [
    getClientList,
    { loading: clientLoading, data: clientData }
  ] = useLazyQuery(GET_BASIC_CLIENT_DATA, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    variables: {
      listInfo: {
        sort: [
          {
            attribute: 'name',
            order: 'ASC'
          }
        ],
        condition: showOnlyActiveClients
          ? {
              joinOperator: 'AND',
              operands: [
                {
                  attribute: 'stage',
                  operatorValue: 'placeholder.client.status.active'
                }
              ]
            }
          : undefined
      }
    },
    onCompleted({ getClientList }) {
      if (getClientList?.clients) {
        const options = getClientList?.clients?.map((el) => {
          return {
            ...el,
            name: el?.name,
            value: el?.name,
            label: el?.name
          };
        });
        setClientOptions(options || []);
      }
    }
  });
  const [getDepartments, { loading: departmentLoading }] = useLazyQuery(
    GET_BUSINESS_FUNCTION_LIST,
    {
      client: AccountClient,
      fetchPolicy: 'cache-and-network',
      onCompleted: function(res) {
        const data = res?.getBusinessFunctionList;
        const options = data?.map((el) => {
          return {
            ...el,
            name: el?.name,
            value: el?.businessFunctionId,
            label: el?.name
          };
        });
        setDepartmentOptions(options);
      }
    }
  );

  const addCondition = (condition, attribute, value) => {
    if (value) {
      condition.operands.push({
        attribute,
        operator: 'is',
        value
      });
    }
  };

  const clientCondition = { joinOperator: 'AND', operands: [] };

  if (isMSPProduct) {
    addCondition(
      clientCondition,
      'client.accountId',
      selectedClient?.accountId
    );
  } else {
    addCondition(
      clientCondition,
      'user.businessFunction.businessFunctionId',
      selectedDepartment?.businessFunctionId
    );
  }
  addCondition(clientCondition, 'site.id', selectedSite?.id);

  const [
    getClientUsers,
    { loading: requestersLoading, data: requestersData }
  ] = useLazyQuery(GET_USERS_ASSOCIATIONS_LIST, {
    client: AccountClient,
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        listInfo: {
          sort: [
            {
              attribute: 'user.name',
              order: 'ASC'
            }
          ],
          condition: clientCondition.operands.length ? clientCondition : null
        }
      }
    },

    onCompleted({ getClientUserAssociationsList }) {
      if (getClientUserAssociationsList?.associations) {
        const options = getClientUserAssociationsList?.associations.map(
          (el) => {
            el = el.user;
            return {
              ...el,
              name: el?.name,
              value: el?.name,
              label: el?.name
            };
          }
        );
        setRequesterOptions(options || []);
      }
    }
  });
  const {
    getClientSites,
    data: sites,
    loading: sitesLoading,
    extraVariables = {}
  } = useSiteListForDropdown();

  function getSites() {
    getClientSites(selectedClient?.accountId);
  }
  useEffect(() => {
    if (sites) {
      if (extraVariables.isOnClientChange) {
        const hq = sites?.find((site) => site?.hq);
        if (!isEmpty(hq)) {
          setSelectedSite(hq);
        }
        return;
      }
      const options = sites?.map((el) => {
        return {
          ...el,
          name: el?.name,
          value: el?.name,
          label: el?.name
        };
      });
      setSiteOptions(options || []);
    }
  }, [sites]);

  const save = () => {
    let updatedProperties = {};
    const isClientChanged = !isEqual(
      selectedClient?.accountId,
      client?.accountId
    );
    const isSiteChanged = !isEqual(selectedSite?.id, site?.id);
    const isDepartmentChanged = !isEqual(
      selectedDepartment?.businessFunctionId,
      department?.businessFunctionId
    );
    if (isDepartmentChanged) {
      const departmentInput = selectedDepartment?.businessFunctionId
        ? {
            businessFunctionId: selectedDepartment?.businessFunctionId
          }
        : null;
      updatedProperties = {
        ...updatedProperties,
        department: departmentInput
      };
    }
    if (isClientChanged) {
      const clientInput = {
        accountId: selectedClient?.accountId,
        name: selectedClient?.name
      };
      updatedProperties = {
        ...updatedProperties,
        client: clientInput
      };
    }

    if (isRequesterChanged.current) {
      const requesterInput = selectedRequester?.userId
        ? {
            userId: selectedRequester?.userId,
            firstName: selectedRequester?.firstName,
            lastName: selectedRequester?.lastName,
            name: selectedRequester?.name
          }
        : null;
      if (assetUiSection) {
        updatedProperties = {
          ...updatedProperties,
          owner: selectedRequester?.userId
            ? {
                userId: requesterInput.userId
              }
            : {} // todo: discuss with backend and make changes (should be going as null)
        };
      } else {
        updatedProperties = {
          ...updatedProperties,
          requester: requesterInput
        };
      }
    }

    if (isSiteChanged) {
      const siteInput = selectedSite?.id
        ? {
            id: selectedSite?.id,
            name: selectedSite?.name
          }
        : null;
      updatedProperties = {
        ...updatedProperties,
        site: siteInput
      };
    }

    if (!isEmpty(updatedProperties)) {
      onSave(updatedProperties);
    }
    props?.toggleEditMode();
  };

  const onClientChange = (client) => {
    setSelectedClient(client);
    setSelectedRequester(null);
    if (client?.accountId) {
      getClientSites(client.accountId, { isOnClientChange: true });
    }
  };

  const onSiteChange = (site) => {
    setSelectedSite(site);
    // setSelectedRequester(null);
  };

  const onRequesterChange = (field, userId, req) => {
    setSelectedRequester(req);
    isRequesterChanged.current = true;
    const requesterSite = req?.associations?.[0]?.site;
    if (
      isMSPProduct &&
      !isEmpty(requesterSite) &&
      requesterSite?.id !== selectedSite?.id
    ) {
      setSelectedSite({
        ...requesterSite,
        label: requesterSite?.name,
        value: requesterSite?.name
      });
    }
    if (!isMSPProduct) {
      const departmentData = req.businessFunction;
      if (departmentData) {
        setSelectedDepartment({
          ...departmentData,
          name: departmentData?.name,
          label: departmentData?.name,
          value: departmentData?.businessFunctionId
        });
      } else {
        setSelectedDepartment(null);
      }
    }
  };
  const onDepartmentChange = (dep) => {
    setSelectedDepartment(dep);
    setSelectedRequester(null);
  };

  let fields = [
    {
      name: 'client',
      label: getTranslatedText(intl, 'common.client'),
      icon: 'ClientLogo',
      value: selectedClient?.name || null,
      placeholder: getTranslatedText(
        intl,
        'rmm.agentDownload.modal.clientPlaceholder'
      ),
      loadOptions: getClientList,
      options: clientOptions,
      loading: clientLoading,
      onChange: onClientChange,
      productToCheck: COMMON_CONSTANTS.MSP,
      isDisabled: disableClientEdit
    },
    {
      name: 'site',
      label: getTranslatedText(intl, 'app.common.site'),
      icon: 'Location',
      value: selectedSite?.name || null,
      placeholder: getTranslatedText(
        intl,
        'rmm.agentDownload.modal.sitePlaceholder'
      ),
      loadOptions: getSites,
      options: siteOptions,
      loading: sitesLoading,
      onChange: onSiteChange,
      isDisabled: disableSiteEdit
    },
    {
      name: 'department',
      label: getTranslatedText(intl, 'common.department'),
      icon: 'Department',
      value: selectedDepartment?.name || null,
      placeholder: getTranslatedText(intl, 'common.select.department'),
      loadOptions: getDepartments,
      options: departmentOptions,
      loading: departmentLoading,
      onChange: onDepartmentChange,
      productToCheck: COMMON_CONSTANTS.IT,
      isClearable: true
    },
    {
      name: 'Requester',
      label: getTranslatedText(
        intl,
        isMSPProduct ? 'ticket.details.header.req' : 'common.user'
      ),
      icon: 'Requester',
      value: selectedRequester?.name || null,
      placeholder: getTranslatedText(
        intl,
        isMSPProduct ? 'mobile.chooseRequester' : 'itsm.choose.user'
      ),
      loadOptions: getClientUsers,
      options: requesterOptions,
      loading: requestersLoading,
      onChange: onRequesterChange,
      isClearable: requesterClearable,
      component: UserSelect,
      componentProps: {
        isClearable: requesterClearable,
        client: selectedClient,
        site: selectedSite,
        department: selectedDepartment,
        updateField: onRequesterChange,
        property: 'userId',
        field: 'requester',
        disableClientEdit: true,
        defaultValue: !isEmpty(selectedRequester)
          ? {
              ...selectedRequester,
              name: selectedRequester?.name,
              label: selectedRequester?.name,
              value: selectedRequester?.name
            }
          : null,
        value: !isEmpty(selectedRequester)
          ? {
              ...selectedRequester,
              name: selectedRequester?.name,
              label: selectedRequester?.name,
              value: selectedRequester?.name
            }
          : null,
        sortList: true,
        noMargin: true,
        createRequester: hideCreateRequester ? false : true,
        hideIcon: true,
        allowAdditionalProps: true,
        noMargin: true,
        fetchBefore: true,
        dropDownComponents: { IndicatorSeparator, DropdownIndicator },
        menuStyles: {
          maxHeight: '220px'
        }
      }
    }
  ];

  if (!isEmpty(popupFieldsToShow)) {
    fields = fields.filter((field) => {
      const fieldName = field?.name;
      const hideField = !popupFieldsToShow.includes(fieldName);
      return !hideField;
    });
  }

  return (
    <div className="w100 p10 min-width-300">
      {fields.map((field) => {
        if (field.productToCheck && field.productToCheck !== currentProduct) {
          return false;
        }
        const CustomComponent = field?.component;
        const customProps = field?.componentProps || {};

        if (CustomComponent) {
          return (
            <>
              <div className="mb10 w100">
                <div className="mb5">
                  <TextStyle>{field?.label}</TextStyle>
                </div>
                <div className="flex actr g5 w100">
                  {!hidePopupIcons && (
                    <div>
                      <SVGIcon name={field?.icon} />
                    </div>
                  )}

                  <div className="w100">
                    <CustomComponent {...customProps} />
                  </div>
                </div>
              </div>
            </>
          );
        }
        return (
          <div className="mb10 w100">
            <div className="mb5">
              <TextStyle>{field?.label}</TextStyle>
            </div>
            <div className="flex actr g5 w100">
              <div>
                <SVGIcon name={field?.icon} />
              </div>
              <div className="w100">
                <Select
                  value={
                    field?.value
                      ? {
                          name: field?.value,
                          label: field?.value,
                          value: field?.value
                        }
                      : null
                  }
                  isClearable={field.isClearable}
                  onChange={field?.onChange}
                  options={field?.options || []}
                  isDisabled={field?.isDisabled}
                  components={{ IndicatorSeparator, DropdownIndicator }}
                  classNamePrefix="value_selector"
                  menuPlacement="auto"
                  placeholder={field?.placeholder}
                  // menuPortalTarget={document.body}
                  closeMenuOnScroll={(e) => {
                    return (
                      e.target &&
                      e.target.classList &&
                      !(e.target.classList.value.indexOf('menu-list') > -1)
                    );
                  }}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (base) => ({ ...base, zIndex: 9999 })
                  }}
                  onMenuOpen={field?.loadOptions}
                  isLoading={field?.loading}
                />
              </div>
            </div>
          </div>
        );
      })}

      <div className="flex actr jend g5 mt20 mb10">
        <div>
          <Button onClick={onCancel}>
            <TextStyle>
              <FormattedMessage id="common.cancel" />
            </TextStyle>
          </Button>
        </div>
        <div>
          <Button onClick={save} primary>
            <TextStyle>
              <FormattedMessage id="common.save" />
            </TextStyle>
          </Button>
        </div>
      </div>
    </div>
  );
};

const TicketDetilsClientFields = (props) => {
  const {
    openRequesterModal,
    requesterClearable,
    isUpdateDeptPayload,
    assetUiSection,
    hideCreateRequester = false,
    hideSite = false,
    avatarSize,
    smallFont = true,
    classNames,
    enableEdit = true,
    popupFieldsToShow = [],
    hidePopupIcons = false,
    disableClientEdit = false,
    disableSiteEdit = false,
    showOnlyActiveClients
  } = props;
  const intl = useIntl();
  const isMSP = useUserMSPCheckHook();
  const requesterName = getRequesterName(props?.requester);
  const clientName = props?.client?.name;
  const departmentName = props.department?.name;
  const ticketContext = useContext(TicketContext);
  const isRequesterEditAuthorised = useIsAuthorisedHook('users.edit');
  const [editMode, setEditMode] = useState(false);
  const editTippyRef = React.useRef(null);
  const [showAPIProgress, setShowAPIProgress] = useState(false);
  const [progressState, setProgressState] = useState(null);
  const permission = props?.editPermission || 'tickets.edit';
  const isEditAuthorised = useIsAuthorisedHook(permission) && enableEdit;
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const [isTippyVisible, setIsTippyVisible] = useState(false);

  const toggleTippy = () => {
    setIsTippyVisible((prevState) => !prevState);
  };

  const toggleEditMode = () => {
    if (isEditAuthorised) {
      if (!editTippyRef.current._tippy.state?.isVisible) {
        setEditMode(true);
        editTippyRef.current._tippy.show();
      } else if (editTippyRef.current._tippy.state?.isVisible) {
        editTippyRef.current._tippy.hide();
        setEditMode(false);
      }
    }
  };

  const openClientTab = () => {
    ticketContext.openTab({
      module: 'CLIENT',
      data: {
        id: props?.client?.accountId,
        name: props?.client?.name,
        value: props?.client?.name
      }
    });
  };
  const openSiteTab = () => {
    ticketContext.openTab({
      module: 'IT_INSIGHT',
      url: `/itsm/insights/site/${props?.site?.id}`
    });
  };

  const onSaveComplete = (callback) => {
    setProgressState(callback);
    props?.refetchTicket();
    EventEmitter.dispatch('refreshRecipients');
    setTimeout(() => {
      setShowAPIProgress(false);
      setProgressState(null);
    }, 2000);
  };

  const onSave = (properties) => {
    if (
      props?.assetUiSection &&
      props?.enableConfirmation &&
      (properties?.client || properties?.site)
    ) {
      props.propertiesRef.current = properties;
      props?.toggleClientChangeConfirmation();
    } else {
      onUpdateSave(properties);
    }
  };

  const onUpdateSave = (properties) => {
    setShowAPIProgress(true);
    setProgressState('loading');
    props?.updateProperties(properties, onSaveComplete);
  };

  const onModalConfirm = () => {
    props?.toggleClientChangeConfirmation();
    if (props?.propertiesRef.current) {
      onUpdateSave(props.propertiesRef.current);
      props.propertiesRef.current = null;
    }
  };

  function AssetSection() {
    // TODO itsm
    return (
      <div className="flex g10 fsize12 actr">
        <AccessControl
          productToCheck={COMMON_CONSTANTS.MSP}
          render={
            <div className="flex actr g5">
              <div className="flex">
                <SVGIcon name="ClientLogo" size="sm" />
              </div>
              <Link fontWeight="bold" removeUnderline onClick={openClientTab}>
                {clientName}
              </Link>
            </div>
          }
        />
        <div className="flex actr g5">
          <div className="flex">
            <SVGIcon name="Location" size="sm" />
          </div>
          <div>
            <TextStyle weight="Medium">{props?.site?.name}</TextStyle>
          </div>
        </div>
        <AccessControl
          productToCheck={COMMON_CONSTANTS.IT}
          render={
            <div className="flex actr g5">
              <div className="flex">
                <SVGIcon name="Department" size="sm" />
              </div>
              <TextStyle weight="Medium">
                {departmentName ||
                  getTranslatedText(intl, 'common.select.department')}
              </TextStyle>
            </div>
          }
        />
        <div className="flex actr g5">
          <Avatar initials={requesterName?.[0] || null} size="extraSmall" />
          <div>
            {requesterName ? (
              <>
                {isRequesterEditAuthorised && !props?.requester?.forgotten ? (
                  <Link
                    fontWeight="bold"
                    removeUnderline
                    onClick={openRequesterModal}
                  >
                    {requesterName}
                  </Link>
                ) : (
                  <TextStyle weight="Medium">{requesterName}</TextStyle>
                )}
              </>
            ) : (
              <TextStyle variation="subdued" weight="Medium">
                <FormattedMessage
                  id={
                    isMSPProduct ? 'mobile.chooseRequester' : 'itsm.choose.user'
                  }
                />
              </TextStyle>
            )}
          </div>
        </div>

        {showAPIProgress && (
          <>
            <div className="sync-loader ml-10">
              {progressState === 'loading' && <ApiProgressLoader isLoading />}
              {progressState === 'success' && <ApiProgressLoader isSuccess />}
              {progressState === 'error' && (
                <ApiProgressLoader isSuccess={false} />
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <Tippy
        content={
          <>
            {editMode && (
              <Card border="secondary" noShadow>
                <ClientDetailsEdit
                  client={props?.client}
                  requester={props?.requester}
                  site={props?.site}
                  department={props?.department}
                  onCancel={toggleEditMode}
                  onSave={onSave}
                  toggleEditMode={toggleEditMode}
                  requesterClearable={requesterClearable}
                  isUpdateDeptPayload={isUpdateDeptPayload}
                  assetUiSection={assetUiSection}
                  hideCreateRequester={hideCreateRequester}
                  popupFieldsToShow={popupFieldsToShow}
                  hidePopupIcons={hidePopupIcons}
                  disableClientEdit={disableClientEdit}
                  disableSiteEdit={disableSiteEdit}
                  showOnlyActiveClients={showOnlyActiveClients}
                />
              </Card>
            )}
          </>
        }
        duration={250}
        delay={500}
        interactive
        placement="bottom-start"
        arrow={false}
        allowHTML
        theme="light"
        appendTo={document.body}
        trigger="manual"
        zIndex={399}
        open={editMode}
        ref={(node) => {
          editTippyRef.current = node;
        }}
        hideOnClick={false}
      >
        <div
          className={`ticket-client-details flex g5 actr p5 br-5 visibilityContainer ${
            props?.readOnly || !isEditAuthorised ? '' : 'hover-bg'
          }`}
          id="ticket-client-details"
          onClick={toggleEditMode}
        >
          <Tippy
            content={
              <>
                {isTippyVisible && (
                  <>
                    <TippyContent
                      client={props?.client}
                      requester={props?.requester}
                      site={props?.site}
                      department={props?.department}
                    />
                  </>
                )}
              </>
            }
            duration={250}
            delay={500}
            interactive
            placement="bottom-start"
            arrow={false}
            allowHTML
            theme="light"
            appendTo={document.body}
            onShow={toggleTippy}
            onClickOutside={toggleTippy}
            onHide={toggleTippy}
          >
            {assetUiSection ? (
              <AssetSection />
            ) : (
              <div
                className={`flex g5 actr ${smallFont &&
                  'fsize12'} ${classNames || ''}`}
              >
                <Avatar
                  initials={requesterName?.[0] || null}
                  size={avatarSize || 'extraSmall'}
                />
                <div>
                  {requesterName ? (
                    <>
                      {isRequesterEditAuthorised &&
                      !props?.requester?.forgotten ? (
                        <Link
                          fontWeight="bold"
                          removeUnderline
                          onClick={openRequesterModal}
                        >
                          {requesterName}
                        </Link>
                      ) : (
                        <TextStyle weight="Medium">{requesterName}</TextStyle>
                      )}
                    </>
                  ) : (
                    <TextStyle variation="subdued" weight="Medium">
                      NA
                    </TextStyle>
                  )}
                </div>
                {!hideSite && (
                  <>
                    <TextStyle variation="subdued">
                      <FormattedMessage id="common.from" />
                    </TextStyle>

                    <AccessControl
                      productToCheck={COMMON_CONSTANTS.MSP}
                      render={
                        <div>
                          <Link
                            fontWeight="bold"
                            removeUnderline
                            onClick={openClientTab}
                          >
                            {clientName}
                          </Link>
                        </div>
                      }
                    />
                    <AccessControl
                      productToCheck={COMMON_CONSTANTS.IT}
                      render={
                        <TextStyle weight="Medium">
                          {departmentName || 'NA'}
                        </TextStyle>
                      }
                    />
                    <div className="flex">
                      <SVGIcon name="Location" size="sm" />
                    </div>
                    <div className="ellipsis mw-160">
                      <Tooltip
                        title={props?.site?.name}
                        delay={200}
                        duration={250}
                        position="bottom"
                        distance={10}
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                      >
                        <AccessControl
                          productToCheck={COMMON_CONSTANTS.MSP}
                          render={
                            <TextStyle weight="Medium">
                              {props?.site?.name}
                            </TextStyle>
                          }
                        />
                        <AccessControl
                          productToCheck={COMMON_CONSTANTS.IT}
                          render={
                            <div>
                              <Link
                                fontWeight="bold"
                                removeUnderline
                                onClick={openSiteTab}
                              >
                                {props?.site?.name}
                              </Link>
                            </div>
                          }
                        />
                      </Tooltip>
                    </div>
                  </>
                )}

                {showAPIProgress && (
                  <>
                    <div className="sync-loader ml-10">
                      {progressState === 'loading' && (
                        <ApiProgressLoader isLoading />
                      )}
                      {progressState === 'success' && (
                        <ApiProgressLoader isSuccess />
                      )}
                      {progressState === 'error' && (
                        <ApiProgressLoader isSuccess={false} />
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </Tippy>
          {!props?.readOnly && isEditAuthorised && isMSP && (
            <div className="flex pointer visibilityActions">
              <SVGIcon name="Edit" size="sm" />
            </div>
          )}
        </div>
      </Tippy>
      <ChangeConfirmationModal
        activeModal={props?.clientChangeConfirmation}
        toggleModalActive={props?.toggleClientChangeConfirmation}
        handleChange={onModalConfirm}
        confirmationModalContent="asset.details.clientChange.modal.primaryText"
      />
    </>
  );
};

export default withErrorBoundary(TicketDetilsClientFields);
